import { apiClient } from '../client'
import type { ApiPaginatedResponse, ApiSuccessResponse } from '../../types/api'
import type {
  BookmarkResponse,
  CaseActivity,
  CaseComment,
  CaseFile,
  CaseFileDownloadResponse,
  CaseFiltersResponse,
  CaseFormPayload,
  CaseListParams,
  CaseReviewer,
  CaseReviewerPickerResponse,
  MedicalCase,
} from '../../types/case'

export const caseService = {
  async list(params?: CaseListParams) {
    const { data } = await apiClient.get<
      ApiPaginatedResponse<MedicalCase> | ApiSuccessResponse<MedicalCase[]>
    >('/cases', {
      params: params
        ? {
            ...params,
            // Laravel `$request->boolean()` — send 1/omit rather than JS true/false
            bookmarked: params.bookmarked ? 1 : undefined,
            only_trashed: params.only_trashed ? 1 : undefined,
            with_trashed: params.with_trashed ? 1 : undefined,
          }
        : undefined,
    })
    return data
  },

  async show(id: string) {
    const { data } = await apiClient.get<ApiSuccessResponse<MedicalCase>>(`/cases/${id}`)
    return data
  },

  async create(payload: CaseFormPayload) {
    const { data } = await apiClient.post<ApiSuccessResponse<MedicalCase>>('/cases', payload)
    return data
  },

  async update(id: string, payload: CaseFormPayload) {
    const { data } = await apiClient.put<ApiSuccessResponse<MedicalCase>>(`/cases/${id}`, payload)
    return data
  },

  async remove(id: string) {
    const { data } = await apiClient.delete<ApiSuccessResponse<null>>(`/cases/${id}`)
    return data
  },

  async restore(id: string) {
    const { data } = await apiClient.post<ApiSuccessResponse<MedicalCase>>(`/cases/${id}/restore`)
    return data
  },

  async preview(id: string) {
    const { data } = await apiClient.get<ApiSuccessResponse<MedicalCase>>(`/cases/${id}/preview`)
    return data
  },

  async filters() {
    const { data } = await apiClient.get<ApiSuccessResponse<CaseFiltersResponse>>('/cases/filters')
    return data
  },

  async publish(id: string) {
    const { data } = await apiClient.post<ApiSuccessResponse<MedicalCase>>(`/cases/${id}/publish`)
    return data
  },

  async schedule(id: string, payload?: { procedure_date?: string | null }) {
    const { data } = await apiClient.put<ApiSuccessResponse<MedicalCase>>(
      `/cases/${id}/schedule`,
      payload,
    )
    return data
  },

  async revertToDraft(id: string) {
    const { data } = await apiClient.post<ApiSuccessResponse<MedicalCase>>(
      `/cases/${id}/revert-to-draft`,
    )
    return data
  },

  async openReview(id: string, payload?: { reviewer_ids?: string[] }) {
    const { data } = await apiClient.post<ApiSuccessResponse<MedicalCase>>(
      `/cases/${id}/open-review`,
      payload,
    )
    return data
  },

  async closeReview(id: string) {
    const { data } = await apiClient.post<ApiSuccessResponse<MedicalCase>>(
      `/cases/${id}/close-review`,
    )
    return data
  },

  async archive(id: string) {
    const { data } = await apiClient.post<ApiSuccessResponse<MedicalCase>>(`/cases/${id}/archive`)
    return data
  },

  async listReviewers(caseId: string) {
    const { data } = await apiClient.get<ApiSuccessResponse<CaseReviewer[]>>(
      `/cases/${caseId}/reviewers`,
      { skipErrorToast: true },
    )
    return data
  },

  async reviewerPicker(caseId: string) {
    const { data } = await apiClient.get<ApiSuccessResponse<CaseReviewerPickerResponse>>(
      `/cases/${caseId}/reviewers/picker`,
      { skipErrorToast: true },
    )
    return data
  },

  async syncReviewers(caseId: string, payload: { reviewer_ids: string[] }) {
    const { data } = await apiClient.post<ApiSuccessResponse<CaseReviewer[]>>(
      `/cases/${caseId}/reviewers`,
      payload,
    )
    return data
  },

  async reviewInbox(params?: { page?: number; per_page?: number; doctor_id?: string }) {
    const { data } = await apiClient.get<
      ApiPaginatedResponse<MedicalCase> | ApiSuccessResponse<MedicalCase[]>
    >('/cases/review-inbox', { params })
    return data
  },

  async listComments(caseId: string, params?: { channel?: 'review' | 'national' }) {
    const { data } = await apiClient.get<ApiSuccessResponse<CaseComment[]>>(
      `/cases/${caseId}/comments`,
      { params, skipErrorToast: true },
    )
    return data
  },

  async createComment(
    caseId: string,
    payload: {
      body: string
      channel?: 'review' | 'national'
      tag?: string | null
      parent_id?: string | null
      is_anonymous?: boolean
    },
  ) {
    const { data } = await apiClient.post<ApiSuccessResponse<CaseComment>>(
      `/cases/${caseId}/comments`,
      payload,
    )
    return data
  },

  async updateComment(caseId: string, commentId: string, payload: { body: string }) {
    const { data } = await apiClient.put<ApiSuccessResponse<CaseComment>>(
      `/cases/${caseId}/comments/${commentId}`,
      payload,
    )
    return data
  },

  async deleteComment(caseId: string, commentId: string) {
    const { data } = await apiClient.delete<ApiSuccessResponse<null>>(
      `/cases/${caseId}/comments/${commentId}`,
    )
    return data
  },

  async listFiles(caseId: string) {
    const { data } = await apiClient.get<ApiSuccessResponse<CaseFile[]>>(`/cases/${caseId}/files`, {
      skipErrorToast: true,
    })
    return data
  },

  async uploadFile(caseId: string, formData: FormData) {
    const { data } = await apiClient.post<ApiSuccessResponse<CaseFile>>(
      `/cases/${caseId}/files`,
      formData,
    )
    return data
  },

  async uploadFiles(caseId: string, formData: FormData) {
    const { data } = await apiClient.post<ApiSuccessResponse<CaseFile[]>>(
      `/cases/${caseId}/files/batch`,
      formData,
    )
    return data
  },

  async deleteFile(caseId: string, fileId: string) {
    const { data } = await apiClient.delete<ApiSuccessResponse<null>>(
      `/cases/${caseId}/files/${fileId}`,
    )
    return data
  },

  async downloadFile(caseId: string, fileId: string) {
    const { data } = await apiClient.get<ApiSuccessResponse<CaseFileDownloadResponse>>(
      `/cases/${caseId}/files/${fileId}/download`,
    )
    return data
  },

  async toggleBookmark(caseId: string, bookmark: 0 | 1) {
    const { data } = await apiClient.post<ApiSuccessResponse<BookmarkResponse>>(
      `/cases/${caseId}/bookmark`,
      { bookmark },
    )
    return data
  },

  async listActivity(caseId: string) {
    const { data } = await apiClient.get<ApiSuccessResponse<CaseActivity[]>>(
      `/cases/${caseId}/activity`,
      { skipErrorToast: true },
    )
    return data
  },
}
